import {
  OnInit,
  Component,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import {
  catchError,
  distinctUntilChanged,
  filter,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { AuthUser } from '@shared/signup/models';
import { ResourceSettings } from '@shared/restaurant/models';
import { Resource } from '@shared/resource/models';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  GlobalErrorHandler,
} from '@tablesready/common-ui';
import {
  ResourceApiService,
  ResourcesService,
  ServicesService,
  WhoService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../../../services';
import { AddEditResourceComponent } from '../../modals/add-edit-resource/add-edit-resource.component';
import { ResourcesListComponent } from './resources-list';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { ResourcesPageService } from '../../rosources-page.service';

@Component({
  selector: 'tablesready-resources-page',
  templateUrl: './resources-page.component.html',
  styleUrls: ['./resources-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GlobalErrorHandler],
})
export class ResourcesPageComponent implements OnInit, OnDestroy {
  @ViewChild('resourcesList') resourceListCmp: ResourcesListComponent;
  @Input() resources: Resource[] = [];

  icon = AppIcon;
  color = AppIconColor;
  size = AppIconSize;

  who: AuthUser;
  resourceSettings: ResourceSettings;
  showCategories = false;
  isReorder = false;
  resourcesCategories = new Set();
  serviceSettings;
  previousSelectedServices: string[];
  selectedServices = [];
  services = [];
  sort: string[] = ['order', 'name', 'category'];

  resourcesForm: UntypedFormGroup;

  private isLocationOnBusinessPlan = false;
  private subscriptions = new Subscription();

  constructor(
    private readonly service: ResourcesPageService,
    private readonly alertsService: AlertsService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly modalController: FrontendModalService,
    private readonly whoService: WhoService,
    private readonly fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly resourceApiService: ResourceApiService,
    private readonly errorHandlerService: GlobalErrorHandler,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) {}

  ngOnInit(): void {
    const authSub = this.authRestaurantService.select()
      .pipe(
        filter(user => user.location !== undefined),
        tap((user) => {
          this.resources = [];
          this.who = user;
          this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);
        }),
        switchMap(() => this.resourcesService.selectAllSorted()),
        tap((resources) => {
          this.resources = resources;
          this.resourceSettings = {
            ...this.who.location.resourceSettings,
            services: this.who.location.resourceSettings.services || [],
          };
          const sort = this.resourceSettings.sort || 'name';
          this.showCategories = sort === 'category';
          this.isReorder = sort === 'category' || sort === 'name';
          this.previousSelectedServices = [...(this.resourceSettings.services || [])];
          this.serviceSettings = {...this.who.location.serviceSettings};
          this.createdCategories();
          this.cdr.detectChanges();
        }),
        switchMap(() => this.servicesService.selectAllEnabled()),
        distinctUntilChanged(),
        tap((services) => {
          this.services = services;
          this.selectedServices = this.services
            .filter(service => this.resourceSettings.services.includes(service._id))
            .map(item => item._id);
          this.resourcesForm = this.fb.group({
            'enabled': [this.resourceSettings.enabled || false],
            'label': [this.resourceSettings.label || '', Validators.required],
            'showOnPages': [this.resourceSettings.showOnPages || false],
            'sort': [this.resourceSettings.sort || 'name'],
            'services': [this.selectedServices || []],
          });

          const enabledSub = this.resourcesForm.get('enabled').valueChanges
            .pipe(
              filter((value: boolean) => value && !this.isLocationOnBusinessPlan),
              switchMap(() => this.pricePlansRestrictionsModalService.display(this.who)),
              tap(() => {
                this.resourcesForm.get('enabled').setValue(false, { emitEvent: false });
                this.cdr.detectChanges();
              }),
            ).subscribe();
          this.subscriptions.add(enabledSub);

          this.cdr.detectChanges();
        }),
        catchError(error => {
          this.errorHandlerService.handleError(error);

          return of(null);
        }),
      ).subscribe();
    this.subscriptions.add(authSub);
  }

  addEditResource(resource?: Resource): void {
    this.modalController.create<AddEditResourceComponent, Resource>(
      AddEditResourceComponent,
      {
        resource,
        resourceCount: this.resources.length,
        availableServices: this.services,
        categories: Array.from(this.resourcesCategories),
        resourceSettings: this.resourceSettings,
      },
    )
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        if (!res._id) {
          this.saveResource(res);
          return;
        }

        this.updateResource(res);
      });
  }

  updateResource(resource: Resource): void {
    this.resourceApiService.edit(resource)
      .pipe(
        tap(() => {
          this.resources = this.resources.map((item) => {
            if (item._id === resource._id) {
              return { ...resource };
            }

            return item;
          });
          this.cdr.detectChanges();
          this.alertsService.addSuccess('', 'Updated Resource');
        }),
        catchError((error) => {
          this.errorHandlerService.handleError(error);

          return of(null);
        }),
      )
      .subscribe();
  }

  saveResource(resource: Resource): void {
    let order = 0;
    if (this.resources.length) {
      order = (Math.max(...this.resources.map(({ order }) => order)) || 0) + 1;
    }
    resource.order = order;
    this.resourceApiService.create(resource)
      .pipe(catchError((error) => {
        this.errorHandlerService.handleError(error);

        return of(null);
      }))
      .subscribe();
  }

  deleteResource(resource: Resource): void {
    if (!resource._id) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Error:',
        body: 'Resource not yet saved. Nothing to delete.',
      });
    } else {
      this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
        options: {
          title: `Delete ${this.resourceSettings.label.toUpperCase()}`,
          content: `Are you sure you want to delete ${resource.name}?`,
        },
      })
        .pipe(
          take(1),
          filter(res => res?.response),
          switchMap(() => this.resourceApiService.delete(resource._id)),
          tap(() => {
            this.resources = this.resources.filter(item => item._id !== resource._id);
            this.cdr.detectChanges();
            this.alertsService.addSuccess(resource.name, 'Deleted: ');
          }),
          catchError((error) => {
            this.errorHandlerService.handleError(error);

            return of(null);
          }),
        )
        .subscribe();
    }
  }

  toggleEnabled(resource): void {
    if (!resource._id) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Error:',
        body: 'Resource not yet saved. Nothing to toggle.',
      });

      return;
    }

    const { _id, enabled } = resource;
    this.resourceApiService.edit({
      _id,
      enabled,
    })
      .pipe(
        take(1),
        tap(() => {
          this.alertsService.addSuccess(`Resource ${enabled ? 'enabled' : 'disabled'}`, 'Success:');
        }),
        catchError((err) => {
          this.errorHandlerService.handleError(err);

          return of(err);
        }),
      )
      .subscribe();
  }

  updateBulk(updates: Array<Partial<Resource>>): void {
    this.resourceApiService.updateBulk(updates)
      .pipe(catchError((error) => {
        this.errorHandlerService.handleError(error);

        return of(null);
      })).subscribe();
  }

  updateResourceSettings(data: Partial<ResourceSettings>): void {
    this.service.setTabsState(true);
    this.whoService.updateResourceSettings(data)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess('Resources Settings', 'Updated: ');
          this.service.setTabsState(false);
        }),
        catchError(error => {
          this.errorHandlerService.handleError(error);
          this.service.setTabsState(false);

          return of(null);
        }),
      )
      .subscribe();
  }

  createdCategories(): void {
    this.resources.forEach(resource => {
      if (resource.category && resource.category.trim() !== '') {
        this.resourcesCategories.add(resource.category);
      }
    });
  }

  saveResourcesSettings(): void {
    const data = this.resourcesForm.value;
    this.resourcesForm.markAsPristine();
    this.resourceSettings.services = data.services || [];

    this.editServicesInResourceBulk();

    if (!data.sort) {
      delete data.sort;
    }
    data.sortByName = !data.sort;

    this.updateResourceSettings(data);
  }

  editServicesInResourceBulk(): void {
    this.resourcesForm.markAsPristine();
    const removedServices = this.previousSelectedServices
      .filter(id => !this.resourceSettings.services.includes(id));
    const addedServices = this.resourceSettings.services
      .filter(id => !this.previousSelectedServices.includes(id));
    if (removedServices.length === 0 && addedServices.length === 0) {
      return;
    }

    const resources = this.updateServicesInResources(addedServices, removedServices, this.resourceSettings.services);

    this.updateBulk(resources);
  }

  updateServicesInResources(addedServices, removedServices, currentServices): Resource[] {
    return this.resources.map((resource) => {
      if (currentServices.length && !resource.services.length) {
        resource.services = [...currentServices];
      }
      if (removedServices.length) {
        resource.services = resource.services.filter(id => !removedServices.includes(id));
      }
      if (addedServices.length) {
        resource.services = [...new Set(resource.services.concat(addedServices))];
      }
      return resource;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
